import { ReactElement, useCallback, useEffect, useState } from "react";

import './CheckboxDropdown.css';
import CheckboxDropdownItem from "./CheckboxDropdownItem/CheckboxDropdownItem";
import DropdownItem from "../../../models/DropdownItem.model";

const CheckboxDropdown = (props: {
  id: string;
  icon: ReactElement;
  label: string;
  selectedValues: string[];
  items: DropdownItem[];
  onItemChanged: Function;
}) => {
  // States
  const [dropdownElementsList, setDropdownElementsList] = useState<
    ReactElement[]
  >([]);
  const [dropdownFilterText, setDropdownFilterText] = useState<string>("");
  const [selectedDropdownLabels, setSelectedDropdownLabels] = useState<string[]>(props.selectedValues);

  // Variables
  const { onItemChanged } = { ...props };

  const dropdownValueSelectedHandler = useCallback((dropdownLabel: string, dropdownValue: any): void => {
    setSelectedDropdownLabels((prevLabels) => [...prevLabels, dropdownLabel]);

    // Update slicer values
    let selectedVals: string[] = [...props.selectedValues];
    const currValIdx: number = selectedVals.indexOf(dropdownValue);

    // If an option is already checked
    if (currValIdx > -1) {
      if (
        selectedVals.length === 1 || 
        ((selectedVals.includes("All")) && selectedVals.length === 2)
      ) {
        return;
      }

      let tempPrevSelected: string[] = (
        dropdownValue === "All" ? 
        [props.items[0].value] : selectedVals.filter(v => v !== dropdownValue)
      );

      // Uncheck "All" option if an option is unchecked from all the checked options
      tempPrevSelected = tempPrevSelected.filter(v => v !== "All");

      return onItemChanged(tempPrevSelected);
    }

    // If an option is not checked
    if (dropdownValue === "All") {
      // Check all available options if "All" option is checked
      selectedVals = [dropdownValue, ...props.items.map(e => e.value)];
    } else {
      selectedVals = selectedVals.filter(v => v !== "All");
      selectedVals.push(dropdownValue);

      // Automatically check "All" option if the rest of the options are checked
      if (selectedVals.length === props.items.length) {
        selectedVals.unshift("All");
      }
    }

    return onItemChanged(selectedVals);
  },
    [props.selectedValues, props.items, onItemChanged]
  );

  const getDropdownItemElements = useCallback(
    (items: DropdownItem[]): ReactElement[] => {
      const dropdownItemsList: ReactElement[] = items.map((item, idx) => (
        <CheckboxDropdownItem
          key={`${item.name}-${idx}`}
          id={`${item.name}-${idx}`}
          name={`${props.id}-checkbox-dropdown-item-${idx}`}
          label={item.name}
          value={item.value}
          selectedValues={props.selectedValues}
          onValueSelected={dropdownValueSelectedHandler}
        />
      ));

      return dropdownItemsList;
    },
    [props.id, props.selectedValues, dropdownValueSelectedHandler]
  );

  const dropdownFilterTextChangeHandler = useCallback(
    (filterText: string) => {
      const filteredDropdownItems: DropdownItem[] = props.items.filter(
        (item) =>
          item.name.toLowerCase().includes(filterText.trim().toLowerCase())
      );
      const dropdownElementsList = getDropdownItemElements(
        filteredDropdownItems
      );
      setDropdownElementsList(dropdownElementsList);
    },
    [props.items, getDropdownItemElements]
  );

  // Getting initial dropdown item JSX elements
  useEffect(() => {
    const allDropdownItems: ReactElement[] = getDropdownItemElements(props.items);
    setDropdownElementsList(allDropdownItems);

    setSelectedDropdownLabels((prevSelected) => {
      const selectedItems: string[] = props.items.filter(p => props.selectedValues.indexOf(p.value) > -1).map(p => p.name);
      return props.selectedValues.includes("All") ? ["All", ...selectedItems] : selectedItems;
    });
  }, [getDropdownItemElements, props.items, props.selectedValues]);

  // Filter dropdown items after 500 ms of entering filter text
  useEffect(() => {
    const textFilterTimeoutId = setTimeout(() => {
      dropdownFilterTextChangeHandler(dropdownFilterText);
    }, 500);

    return () => clearTimeout(textFilterTimeoutId);
  }, [dropdownFilterText, dropdownFilterTextChangeHandler]);

  return (
    <div className="dropdown d-inline me-1">
      <button
        type="button"
        className="btn btn-sm btn-outline-dark dropdown-toggle"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <span>
          {props.icon && props.icon} &nbsp;{props.label}
        </span>
        {(
          selectedDropdownLabels.includes("All") ? ": All" : (
            selectedDropdownLabels.length > 0 ? `: ${(selectedDropdownLabels.length === 1) ? selectedDropdownLabels[0] : "Multiple"}` : ""
          )
        )}
      </button>

      <ul className="dropdown-menu scrollable-dropdown">
        <li>
          <input
            type="text"
            id={`${props.id}-text-filter`}
            className="form-control form-control-sm ms-2 mb-2"
            style={{ width: "90%" }}
            placeholder={"\u{1F50E} Search..."}
            onChange={(e) => setDropdownFilterText(e.target.value)}
          />
        </li>

        <CheckboxDropdownItem
          key={`all-${props.id}`}
          id={`all-${props.id}--1`}
          name={`${props.id}-checkbox-dropdown-item--1`}
          label="All"
          value="All"
          selectedValues={props.selectedValues}
          onValueSelected={dropdownValueSelectedHandler}
        />

        {dropdownElementsList}
      </ul>
    </div>
  );
};

export default CheckboxDropdown;
